import { useCallback, useEffect, useRef } from "react";
import { useNotepadPerSlideStore } from "@/features/notepad/store/useNotepadPerSlideStore";
import { useSlideStore } from "@/features/tutor/store/useSlideStore";
import { useStudentInputStore } from "@/features/tutor/store/useStudentInputStore";
import { applyInputRevealedDOM } from "@/features/tutor/utils/inputRevealDomHelpers";
import { deriveSafeResumeProgression } from "@/features/tutor/utils/resumeProgression";
import { addResumeDebugEvent } from "@/store/useResumeDebugStore";

// Only has to outlive one thing: the TURN_CHANGE that applying the checkpoint
// emits when it navigates the deck to the saved slide, which the backend would
// read as a barge-in. It used to run 8s because it also had to cover the
// browser-sent continuation prompt; that is gone, and every extra second is a
// second the student's own interrupt is ignored.
const RESUME_TURN_LOCK_WINDOW_MS = 1500;

export function useResumeRehydration({
  clearResumeTurnLock,
  currentSlideIndex,
  getInputKeyByActionId,
  getSlideByIndex,
  lockResumeTurnChanges,
  markInputRevealed,
  armResumePlaybackHold,
  onRehydrateApplied,
  pendingRehydratePayload,
  releaseResumePlaybackHold,
  setCurrentSlideIndex,
  setStudentInput,
  shouldHoldResumePlayback = true,
  shouldInitializeSession,
}) {
  const rehydrateAppliedKeyRef = useRef(null);
  const resumeFragmentFloorRef = useRef(null);

  useEffect(() => {
    if (!pendingRehydratePayload || !shouldInitializeSession) {
      return;
    }

    addResumeDebugEvent("vodemo:rehydrate:effect_start", {
      slide_index: pendingRehydratePayload.slide_index,
      fragment_index: pendingRehydratePayload.fragment_index,
      saved_at: pendingRehydratePayload.saved_at,
      student_inputs_count: Object.keys(
        pendingRehydratePayload.student_inputs || {},
      ).length,
      revealed_fragment_ids_count: Array.isArray(
        pendingRehydratePayload.revealed_fragment_ids,
      )
        ? pendingRehydratePayload.revealed_fragment_ids.length
        : 0,
    });

    const payloadKey = [
      pendingRehydratePayload.saved_at,
      pendingRehydratePayload.slide_index,
      pendingRehydratePayload.fragment_index,
    ].join(":");

    if (rehydrateAppliedKeyRef.current === payloadKey) {
      return;
    }

    // Armed here, not in the boot: this effect runs in the same commit as the
    // one that starts the boot and usually finishes a full request earlier, so
    // anything keyed on "the boot is about to ask for a turn" arms after the
    // restore it was meant to wait for. Below, the deck can also be missing for
    // up to three seconds of retries — that is the window worth holding.
    //
    // Not every payload arrives ahead of its turn, though: the cached-bite
    // continue's turn is already streaming, and holding it clears audio the
    // player is holding. The producer of the payload says which case this is.
    if (shouldHoldResumePlayback) {
      armResumePlaybackHold();
    }

    let attempts = 0;
    let timeoutId = null;

    const applyRehydrate = () => {
      const deck = useSlideStore.getState().currentDeck;
      if (!deck) {
        addResumeDebugEvent("vodemo:rehydrate:deck_missing");
        return false;
      }

      const targetSlideIndex = Number(
        pendingRehydratePayload.slide_index ?? currentSlideIndex,
      );
      if (!Number.isNaN(targetSlideIndex)) {
        setCurrentSlideIndex(targetSlideIndex);
        if (typeof deck.slide === "function") {
          deck.slide(targetSlideIndex);
        }
      }

      const currentSlide = getSlideByIndex(targetSlideIndex);
      const safeResumeProgression = deriveSafeResumeProgression({
        history: pendingRehydratePayload.history,
        slide: currentSlide,
      });

      const checkpointFragmentIndex = Number(
        pendingRehydratePayload.fragment_index ?? -1,
      );
      const historyFragmentIndex = Number.isInteger(
        safeResumeProgression?.lastCompletedFragmentIndex,
      )
        ? safeResumeProgression.lastCompletedFragmentIndex
        : null;
      const fragmentIndex =
        historyFragmentIndex !== null
          ? historyFragmentIndex
          : checkpointFragmentIndex;

      if (
        historyFragmentIndex !== null &&
        historyFragmentIndex !== checkpointFragmentIndex
      ) {
        addResumeDebugEvent("vodemo:rehydrate:clamped_to_history", {
          checkpoint_fragment_index: checkpointFragmentIndex,
          history_fragment_index: historyFragmentIndex,
          last_completed_action_id:
            safeResumeProgression?.lastCompletedActionId ?? null,
        });
      }

      if (fragmentIndex >= 0 && typeof deck.navigateFragment === "function") {
        resumeFragmentFloorRef.current = {
          slideIndex: targetSlideIndex,
          fragmentFloor: fragmentIndex,
        };
        addResumeDebugEvent("vodemo:fragment_guard:set_floor", {
          slide_index: targetSlideIndex,
          floor: fragmentIndex,
        });
        addResumeDebugEvent("vodemo:rehydrate:navigate_fragment", {
          fragment_index: fragmentIndex,
        });
        deck.navigateFragment(fragmentIndex);
      }

      const studentInputsMap = pendingRehydratePayload.student_inputs || {};
      Object.entries(studentInputsMap).forEach(([actionId, value]) => {
        const normalizedActionId = actionId.startsWith("action_")
          ? actionId
          : `action_${actionId}`;
        const inputKey = getInputKeyByActionId(normalizedActionId);
        if (inputKey) {
          setStudentInput(inputKey, value ?? "");
        }
      });

      const revealedFragmentIds = Array.isArray(
        pendingRehydratePayload.revealed_fragment_ids,
      )
        ? pendingRehydratePayload.revealed_fragment_ids.filter(
            (revealedFragmentId) =>
              Number.isInteger(revealedFragmentId) &&
              (fragmentIndex < 0 || revealedFragmentId <= fragmentIndex),
          )
        : [];
      const maxRevealedFragment = revealedFragmentIds.length
        ? Math.max(...revealedFragmentIds)
        : fragmentIndex;

      if (maxRevealedFragment >= 0) {
        const actionIdsToReveal = useSlideStore
          .getState()
          .getInputsToRevealUpToFragment(maxRevealedFragment);
        actionIdsToReveal.forEach((actionId) => {
          const inputKey = getInputKeyByActionId(`action_${actionId}`);
          if (inputKey) {
            markInputRevealed(inputKey);
            applyInputRevealedDOM(`action_${actionId}`);
          }
        });
      }

      useNotepadPerSlideStore
        .getState()
        .syncSlide(
          targetSlideIndex,
          pendingRehydratePayload.notepad_used === true,
        );

      rehydrateAppliedKeyRef.current = payloadKey;

      // The count is per-slide, and this restore moves the slide without going
      // through the navigation controller that would otherwise have reset it.
      useSlideStore.getState().resetSlideActionCount();

      window.animationController?.call("SHRINK");

      addResumeDebugEvent("vodemo:rehydrate:applied", {
        payload_key: payloadKey,
        target_slide_index: targetSlideIndex,
        fragment_index: fragmentIndex,
        checkpoint_fragment_index: checkpointFragmentIndex,
        max_revealed_fragment: maxRevealedFragment,
      });
      console.log(
        `[REHYDRATE_APPLIED] targetSlideIndex=${targetSlideIndex} ` +
          `fragmentIndex=${fragmentIndex}`,
      );
      // Still needed, and for the opposite reason than before. The lock stops
      // the state applied just above from emitting TURN_CHANGE, which the
      // backend reads as an interrupt — and the turn it would kill is now
      // already in flight, not one this browser is about to ask for.
      lockResumeTurnChanges(RESUME_TURN_LOCK_WINDOW_MS);
      addResumeDebugEvent("vodemo:resume_turn_lock:set", {
        payload_key: payloadKey,
        duration_ms: RESUME_TURN_LOCK_WINDOW_MS,
      });
      // The bite is on screen: let the turn the backend already started play.
      releaseResumePlaybackHold("rehydrate_applied");
      onRehydrateApplied();
      return true;
    };

    const tryApply = () => {
      attempts += 1;

      const needsInputMap =
        Object.keys(pendingRehydratePayload.student_inputs || {}).length > 0;
      const inputMapReady =
        !needsInputMap ||
        Object.keys(useStudentInputStore.getState().inputKeyToActionId || {})
          .length > 0;
      const deckReady = !!useSlideStore.getState().currentDeck;

      if ((!deckReady || !inputMapReady) && attempts < 30) {
        if (attempts === 1 || attempts % 5 === 0) {
          addResumeDebugEvent("vodemo:rehydrate:waiting", {
            attempts,
            deckReady,
            inputMapReady,
          });
        }
        timeoutId = setTimeout(tryApply, 100);
        return;
      }

      // The budget is spent. applyRehydrate still gets its turn -- a missing
      // input map is no reason to leave the student on the wrong slide -- but
      // if it cannot run, nothing here will try again, and holding the
      // tutor's audio past the last attempt buys silence and nothing else.
      if (!applyRehydrate()) {
        releaseResumePlaybackHold("restore_gave_up");
      }
    };

    tryApply();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [
    currentSlideIndex,
    getSlideByIndex,
    getInputKeyByActionId,
    lockResumeTurnChanges,
    markInputRevealed,
    onRehydrateApplied,
    pendingRehydratePayload,
    setCurrentSlideIndex,
    setStudentInput,
    armResumePlaybackHold,
    releaseResumePlaybackHold,
    shouldHoldResumePlayback,
    shouldInitializeSession,
  ]);

  useEffect(() => {
    const fragmentGuard = resumeFragmentFloorRef.current;
    if (
      fragmentGuard &&
      Number(fragmentGuard.slideIndex) !== Number(currentSlideIndex)
    ) {
      addResumeDebugEvent("vodemo:fragment_guard:clear_on_slide_change", {
        previous_slide_index: fragmentGuard.slideIndex,
        current_slide_index: currentSlideIndex,
      });
      resumeFragmentFloorRef.current = null;
    }
  }, [currentSlideIndex]);

  const clearResumeRuntimeState = useCallback(() => {
    clearResumeTurnLock();
  }, [clearResumeTurnLock]);

  return {
    clearResumeRuntimeState,
    resumeFragmentFloorRef,
  };
}
